import React, { useEffect, useState } from "react";
import {
    Button,
    Empty,
    Input,
    Result,
    Space,
    notification,
    Descriptions,
    Tree,
    Flex,
    Switch,
} from "antd";
import {
    PaperClipOutlined,
    WarningFilled
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import queryString from 'query-string'
import _ from "lodash";

enum Status {
    isBasic,
    isEmpty,
    isContent,
    isError
}

type QueryError = { level: string, desc: string }

const Query: React.FC<any> = () => {
    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();
    const [status, setStatus] = useState<Status>(Status.isEmpty)
    const [inputUrl, setInputUrl] = useState<string>(localStorage.getItem('Query.url') || '')
    const [inputErid, setInputErid] = useState<string>(localStorage.getItem('Query.erid') || '')

    const [urlResult, setUrlResult] = useState<string>('')
    const [urlProtocol, setUrlProtocol] = useState<string>('')
    const [urlDomain, setUrlDomain] = useState<string>('')
    const [urlPath, setUrlPath] = useState<string>('')
    const [urlQuery, setUrlQuery] = useState<queryString.ParsedUrl>()
    const [urlQueryOrder, setUrlQueryOrder] = useState<string[]>([])
    const [urlQueryDisabled, setUrlQueryDisabled] = useState<string[]>([])
    const [errors, setErrors] = useState<QueryError[]>([])

    const handleUrlChange = (value: string) => {
        localStorage.setItem('Query.url', value)
        setInputUrl(value)
    }
    const handleEridChange = (value: string) => {
        localStorage.setItem('Query.erid', value)
        setInputErid(value)
    }

    const handleCopyClick = async (e: any) => {
        try {
            await navigator.clipboard.writeText(urlResult)
            api.success({
                message: t(`ExportPostStats.copyToClipboard.successMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportPostStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportPostStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    useEffect(() => {
        setErrors([])
        setUrlResult('')
        if (!!inputUrl.length) {
            setStatus(Status.isContent)
        } else {
            setStatus(Status.isEmpty)
            return
        }
        try {
            const errors: QueryError[] = []
            let input = inputUrl.trim()
            const erid = inputErid.trim()
            // protocol error
            if (input.startsWith('/')) {
                return setErrors([{ level: 'critical', desc: 'Неправильный формат ссылки!' }])
            }
            // other protocols
            if (/^(mailto|tel|skype|viber|whatsapp):/ui.test(input)) {
                return setErrors([{ level: 'critical', desc: 'К данному протоколоу нельзя добавить параметры!' }])
            }
            // error cases
            if (/^[^a-z]{1,}([a-z]+):/ui.test(input)) {
                return setErrors([{ level: 'critical', desc: 'Проверьте ссылку, возможно перед ссылкой присуствуют лишние символы!' }])
            }
            if (/\s/ui.test(input)) {
                return setErrors([{ level: 'critical', desc: 'Проверьте ссылку, в ссылке присуствуют пробелы!' }])
            }
            if (!/^([!#$&-;=?-[\]_a-zа-я~]|%[0-9a-f]{2,20})+$/ui.test(input)) {
                return setErrors([{ level: 'critical', desc: 'Проверьте ссылку, В ссылке присуствуют запрещенные символы!' }])
            }
            if (erid.length && !/^[a-z0-9]+$/ui.test(erid)) {
                errors.push({ level: 'warning', desc: 'Проверьте ERID, В ERID есть запрещенные символы!' })
            }
            if (/[а-я]+/ui.test(input)) {
                errors.push({ level: 'warning', desc: 'Проверьте ссылку, в ссылке присуствуют русские буквы!' })
            }
            // other add https if noy exist
            if (!/^(http|https):\/\//ui.test(input) && !/^(http|https):/ui.test(input) && /^.{1,20}:/ui.test(input)) {
                return setErrors([{ level: 'critical', desc: 'Неизвестный протокол!' }])
            }
            // if protocol not exist
            if (!/^.{1,20}:\/\//ui.test(input) && !input.startsWith('//')) {
                errors.push({ level: 'warning', desc: 'В ссылке отсуствует протокол!' })
            }
            /* parsing */
            const u = !/^.{1,20}:/ui.test(input)
                ? URL.parse(`https://${input}`)
                : URL.parse(input)
            if (!u) {
                setErrors(errors)
                throw new Error();
            }
            if (/\?/ui.test(u.pathname) || /\?{2,}/ui.test(u.search)) {
                errors.push({ level: 'warning', desc: 'Проверьте ссылку, в ссылке присуствует больше одного знака "?"' })
            }
            const qs = queryString.parseUrl(input, {
                sort: false
            })
            if (erid.length) qs.query.erid = erid
            /* zone */
            if (u.host.split('.').length < 2 || (u.host.split('.').at(-1)?.length || 0) < 1) {
                errors.push({ level: 'error', desc: 'У домена отсуствует зона .ru или любая другая!' })
            }
            if ((u.host.split('.').at(-1)?.length || 0) > 0 && (u.host.split('.').at(-1)?.length || 0) < 2) {
                errors.push({ level: 'warning', desc: `У домена странная зона: "${u.host.split('.').at(-1)}"` })
            }
            /* domain, protocol, path, query */
            setUrlDomain(u.host)
            setUrlProtocol(!/^.{1,20}:/ui.test(input) ? '' : u.protocol.replace(':', ''))
            setUrlPath(u.pathname)
            setUrlQuery(qs)
            setUrlQueryOrder(Object.keys(qs.query))
            setUrlQueryDisabled([])
            setErrors(errors)
        } catch (err: unknown) {
            console.debug("Ivalid url:", inputUrl, err)
        }
    }, [inputUrl, inputErid])

    useEffect(() => {
        if (!!urlQuery) {
            setUrlResult(
                queryString.exclude(
                    queryString.stringifyUrl(urlQuery, { sort: false }),
                    urlQueryDisabled,
                    {
                        sort: (a, b) => urlQueryOrder.indexOf(a) - urlQueryOrder.indexOf(b)
                    }
                )
            )
        }
    }, [urlQuery, urlQueryOrder, urlQueryDisabled])

    const UrlTitle: React.FC<{ isAllowCopy: boolean }> = ({ isAllowCopy }) => (
        <h3>
            {t('Query.url.title') + " "}
            <Button
                type="primary"
                icon={<PaperClipOutlined />}
                disabled={!isAllowCopy}
                onClick={handleCopyClick}
            />
        </h3>
    )
    const QueryErrorTitle: React.FC<QueryError> = (qe) => {
        if (qe.level === 'warning') return <span style={{ color: "orange", fontWeight: "bold" }}>
            <WarningFilled style={{ marginRight: 2 }} />{` ${qe.level.toUpperCase()}`}
        </span>
        if (qe.level === 'critical') return <span style={{ color: "red", fontWeight: "bold" }}>
            <WarningFilled style={{ marginRight: 2 }} />{` ${qe.level.toUpperCase()}`}
        </span>
        if (qe.level === 'error') return <span style={{ color: "red", fontWeight: "bold" }}>
            <WarningFilled style={{ marginRight: 2 }} />{` ${qe.level.toUpperCase()}`}
        </span>
        return <span style={{ color: "red", fontWeight: "bold" }}>
            <WarningFilled style={{ marginRight: 2 }} />{` ${qe.level.toUpperCase()}`}
        </span>
    }

    return (
        <div style={{ height: '100%', position: 'relative', flexDirection: 'row', }}>
            <Space.Compact style={{ width: '100%' }}>
                <Input placeholder={t(`Query.url.placeholder`)}
                    name="inputUrl"
                    size="large"
                    value={inputUrl}
                    onChange={e => handleUrlChange(e.target.value)}
                    onFocus={e => e.target.select()}
                />
                <Input placeholder={t(`Query.erid.placeholder`)}
                    name="inputErid"
                    size="large"
                    value={inputErid}
                    onChange={e => handleEridChange(e.target.value)}
                    onFocus={e => e.target.select()}
                />
            </Space.Compact>
            {status === Status.isEmpty && (
                <Result style={{ paddingTop: '25vh', opacity: .7 }}
                    icon={<Empty description={false} />}
                    status="warning"
                    title={t(`Query.empty`)}
                />
            )}
            {status === Status.isContent && (
                <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', height: 'calc(100% - 70px)' }}>
                    <Space align="end" direction="vertical">
                        <UrlTitle isAllowCopy={!!urlResult.length} />
                    </Space>
                    {errors.map((err, idx) => {
                        return (
                            <Descriptions key={`err-${idx}`}>
                                <Descriptions.Item label={QueryErrorTitle(err)}>
                                    <b>{err.desc}</b>
                                </Descriptions.Item>
                            </Descriptions>
                        )
                    })}
                    {!!urlResult.length && (
                        <div>
                            <Descriptions bordered style={{ marginTop: 15 }}>
                                <Descriptions.Item label={t('Query.url.protocol')}>
                                    {
                                        urlProtocol.length
                                            ? urlProtocol
                                            : <WarningFilled style={{ color: 'orange', marginRight: 2 }} />
                                    }
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Query.url.domain')}>{urlDomain}</Descriptions.Item>
                                <Descriptions.Item label={t('Query.url.path')}>{urlPath}</Descriptions.Item>
                            </Descriptions>
                            {!!Object.keys(urlQuery?.query || {}).length && (
                                <>
                                    <h5 style={{ textAlign: 'left' }}>Query параметры</h5>
                                    <Tree
                                        defaultExpandAll
                                        blockNode
                                        draggable
                                        onDrop={(info) => {
                                            const swapOrder = (list: string[], fromIndex: number, toIndex: number) => {
                                                let calcToIndex = toIndex
                                                if (toIndex > fromIndex) calcToIndex = toIndex - 1
                                                if (toIndex === -1) calcToIndex = toIndex + 1
                                                const arrayCopy = [...list];
                                                const element = arrayCopy.splice(fromIndex, 1)[0];
                                                arrayCopy.splice(calcToIndex, 0, element);
                                                return arrayCopy;
                                            }
                                            if (_.isUndefined(info.dragNodesKeys?.at(0)) || _.isUndefined(urlQuery)) return; // guard 
                                            const newOrder = swapOrder(
                                                urlQueryOrder,
                                                urlQueryOrder.indexOf(info.dragNodesKeys[0].toString()),
                                                info.dropPosition,
                                            )
                                            setUrlQueryOrder(newOrder)
                                        }}
                                        treeData={
                                            Object.entries(urlQuery?.query || {})
                                                .sort((a: [string, any], b: [string, any]) => {
                                                    return urlQueryOrder.indexOf(a.at(0)) - urlQueryOrder.indexOf(b.at(0))
                                                })
                                                .map((param, idx) => ({
                                                    key: String(param.at(0)),
                                                    title: (
                                                        <Flex
                                                            gap="small"
                                                            justify="justify"
                                                            align="flex-start"
                                                        >
                                                            <Switch
                                                                checked={!urlQueryDisabled.includes(String(param.at(0)))}
                                                                onChange={
                                                                    e => !e
                                                                        ? setUrlQueryDisabled([...urlQueryDisabled, String(param.at(0))])
                                                                        : setUrlQueryDisabled([...urlQueryDisabled.filter(e => e !== String(param.at(0)))])
                                                                }
                                                            />
                                                            <Input
                                                                id={`tree-param-${idx}-key`}
                                                                name={`param-${idx}-key`}
                                                                type="text"
                                                                value={String(param.at(0))}
                                                                onChange={(e) => {
                                                                    const origKey: string = String(param.at(0))
                                                                    if (!!urlQuery && Object.hasOwn(urlQuery.query || {}, origKey)) {
                                                                        const newKey = !!e.target.value.length ? e.target.value : origKey
                                                                        if (newKey !== origKey) {
                                                                            setUrlQueryOrder(
                                                                                urlQueryOrder.map((key) => {
                                                                                    return key === origKey ? e.target.value : key
                                                                                })
                                                                            )
                                                                            setUrlQuery({
                                                                                ...urlQuery,
                                                                                query: _.mapKeys(urlQuery.query, (_v, key) => key === origKey ? newKey : key)
                                                                            })
                                                                            setTimeout(() => {
                                                                                const el: HTMLInputElement | null = document.querySelector(`#tree-param-${idx}-key`)
                                                                                if (!_.isNull(el)) el.focus()
                                                                            })
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            <Input
                                                                type="text"
                                                                id={`tree-param-${idx}-value`}
                                                                name={`param-${idx}-value`}
                                                                value={String(param.at(1))}
                                                                onChange={(e) => {
                                                                    const origKey: string = String(param.at(0))
                                                                    if (!!urlQuery && Object.hasOwn(urlQuery.query || {}, origKey)) {
                                                                        const q: queryString.ParsedQuery = { ...urlQuery.query } // be careful - shallow
                                                                        q[origKey] = e.target.value
                                                                        const qs = queryString.stringifyUrl({ ...urlQuery, query: q }, { sort: false })
                                                                        setUrlQuery(queryString.parseUrl(qs, { sort: false }))
                                                                    }
                                                                }}
                                                            />
                                                        </Flex>
                                                    ),
                                                }))
                                        }
                                    />
                                </>
                            )}
                            <Descriptions style={{ textAlign: 'left', marginTop: 10 }}>
                                <Descriptions.Item label={t('Query.url.url')}>{urlResult}</Descriptions.Item>
                            </Descriptions>
                        </div>
                    )}
                </div>
            )
            }
            {contextHolder}
        </div >
    );
}

export default Query
